// How a refund's money goes back. A sale paid one way is refunded that way. A sale paid several
// ways (a card and trade-in credit, cash and a card) can be refunded the same way it was paid: the
// card part back to the card, the cash part from the drawer, store or trade-in credit back to
// store credit. The screen shows the split and the server makes it, with the same math.
import { allocate, formatCents } from './money.js';

export type RefundMethod = 'cash' | 'card' | 'store_credit';
export const REFUND_METHODS: readonly RefundMethod[] = ['cash', 'card', 'store_credit'];
export const isRefundMethod = (v: unknown): v is RefundMethod => typeof v === 'string' && (REFUND_METHODS as readonly string[]).includes(v);

/** One way the money goes back, and how much. */
export interface RefundPart { method: RefundMethod; amount_cents: number }

/** The order the parts are listed in: the card first, then cash, then store credit. */
const ORDER: Record<RefundMethod, number> = { card: 0, cash: 1, store_credit: 2 };

/**
 * What's still owed back for each way the sale was paid: what was paid that way, less what was
 * already refunded that way (by any earlier refund on the receipt).
 */
export function refundableByMethod(
  payments: { method: string; amount_cents: number }[],
  refunded: { method: string; amount_cents: number }[],
): RefundPart[] {
  const left = new Map<RefundMethod, number>();
  for (const p of payments) if (isRefundMethod(p.method)) left.set(p.method, (left.get(p.method) ?? 0) + p.amount_cents);
  for (const r of refunded) if (isRefundMethod(r.method) && left.has(r.method)) left.set(r.method, (left.get(r.method) ?? 0) - r.amount_cents);
  return [...left.entries()]
    .map(([method, cents]) => ({ method, amount_cents: Math.max(0, cents) }))
    .sort((a, b) => ORDER[a.method] - ORDER[b.method]);
}

/**
 * A refund of `total` cents split the way the sale was paid, in proportion to what's still owed
 * back each way (refunding everything that's left gives back exactly what each way paid). The
 * parts always add up to `total`. Nothing owed back any way (a $0 sale): no parts.
 */
export function splitRefund(total: number, left: RefundPart[]): RefundPart[] {
  const owed = left.filter((p) => p.amount_cents > 0);
  if (total <= 0 || !owed.length) return [];
  const parts = allocate(total, owed.map((p) => p.amount_cents));
  return owed.map((p, i) => ({ method: p.method, amount_cents: parts[i] })).filter((p) => p.amount_cents > 0);
}

export const REFUND_METHOD_LABELS: Record<RefundMethod, string> = { cash: 'Cash', card: 'Card', store_credit: 'Store credit' };

/** "in cash", "to the card", "in store credit": how a refund goes back, for the manager's approval. */
export function refundWay(method: string): string {
  return method === 'cash' ? 'in cash' : method === 'card' ? 'to the card' : method === 'store_credit' ? 'in store credit' : `by ${method.replace('_', ' ')}`;
}

/**
 * What the manager approves: "refund $25.00 in cash on receipt #100023", or for a refund split
 * the way it was paid, "refund $500.00 on receipt #100023 ($300.00 to the card, $200.00 in store credit)".
 */
export function refundApprovalText(parts: RefundPart[], total: number, receiptNo: number | string, fallback: RefundMethod = 'cash'): string {
  if (parts.length <= 1) return `refund ${formatCents(total)} ${refundWay(parts[0]?.method ?? fallback)} on receipt #${receiptNo}`;
  return `refund ${formatCents(total)} on receipt #${receiptNo} (${parts.map((p) => `${formatCents(p.amount_cents)} ${refundWay(p.method)}`).join(', ')})`;
}
